import * as React from "react";
import {authorize, IWithRouterProps, Overlay, withRouter} from "@athena-portal/components";
import {lazyInject} from "@athena-portal/core";
import {AnnualPolicyInfo, Policy} from "@ferry/journey-services";
import {Footer} from "@ferry/portal-core";
import {finalize, find, mergeMap} from "rxjs/operators";
import FalconHeader from "../../../..//components/navigation/FalconHeader";
import {PluginTemplates} from "../../../../utils/PluginTemplates";
import {YourDriveAwayCarCoverPanel} from "../yourCarCover/YourDriveAwayCarCoverPanel";
import {Types} from "../../../../Types";
import type {ICustomerPortalService} from "../../../../services/ICustomerPortalService";

interface IParams {
    policyNumber: string;
    [key: string]: string | undefined
}

interface IOldPolicySummaryPageState {
    loadingQueueSize: number;
    policy?: Policy;
}

@authorize
export class OldDriveAwayPolicySummaryPagePlain extends React.Component<IWithRouterProps<IParams>, IOldPolicySummaryPageState> {
    private pluginTemplates = new PluginTemplates();

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    constructor(props: IWithRouterProps<IParams>) {
        super(props);

        this.state = {
            loadingQueueSize: 0
        };
    }

    public componentDidMount() {
        this.pluginTemplates.setPageTitle("Old Drive Away policies");

        this.incrementLoadingQueueSize(() => {
            this.customerPortalService
                .getOldPolicies()
                .pipe(
                    finalize(this.decrementLoadingQueueSize),
                    mergeMap(policies => policies),
                    find(policy => policy.policyNumber === this.props.params.policyNumber),
                )
                .subscribe((foundPolicy?: Policy) => {
                    if (!foundPolicy) {
                        return;
                    }
                    this.setState({ policy: foundPolicy });
                    this.fetchAnnualPolicyInfo(foundPolicy.policyNumber, foundPolicy);
                });
        });
    }

    public render() {
        return (
            <>
                <FalconHeader/>
                <Overlay show={this.isLoading()}/>
                <div className="page-contents">
                    {this.pluginTemplates.renderBackLink("Back", true, "/dashboard/oldPolicies")}
                    <h1 className="page-heading-white">Policy summary</h1>
                    {!this.isLoading() && this.renderPolicy()}
                    {this.pluginTemplates.renderBackLink("Back", false, "/dashboard/oldPolicies")}
                </div>
                <Footer/>
            </>
        );
    }

    private renderPolicy = () => {
        if (this.state.policy && !this.state.policy.annualPolicyInfo) {
            return null;
        }

        if (!this.state.policy) {
            return (
                <div className="panel">
                    <p className="m-0 font-weight-extra-light">Policy does not exist</p>
                </div>
            );
        }

        return (
            <YourDriveAwayCarCoverPanel
                key={this.state.policy.policyNumber}
                policy={this.state.policy}
                vehicleIndex={0}
            />
        );
    };

    private fetchAnnualPolicyInfo = (policyNumber: string, foundPolicy: Policy) => {
        this.incrementLoadingQueueSize(() => {
            this.customerPortalService
                .getAnnualPolicyInfo(policyNumber)
                .pipe(finalize(this.decrementLoadingQueueSize))
                .subscribe((annualPolicyInfo: AnnualPolicyInfo) => {
                    this.setState({
                        policy: { ...foundPolicy, annualPolicyInfo }
                    });
                });
        });
    };

    private incrementLoadingQueueSize = (callback?: () => void) => {
        this.setState(prevState =>
            ({
                loadingQueueSize: prevState.loadingQueueSize + 1,
            }),
            callback,
        );
    };

    private decrementLoadingQueueSize = (callback?: () => void) => {
        if (this.state.loadingQueueSize === 0) {
            return;
        }

        this.setState(prevState =>
            ({
                loadingQueueSize: prevState.loadingQueueSize - 1,
            }),
            callback,
        );
    };

    private isLoading = () => {
        return this.state.loadingQueueSize !== 0;
    };
}

export const OldDriveAwayPolicySummaryPage = withRouter(OldDriveAwayPolicySummaryPagePlain)
